/**
 * Favorites Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  DataSourceConfig 
} from '../types';
import { getCachedFavorites } from "@/lib/stores/favoritesStore";
import { getCachedLibraryItems } from "@/lib/stores/objectsStore";
import { getCachedDocumentItems } from "@/lib/stores/documentStore";
import { getCachedTemplates } from "@/lib/stores/templatesStore";
import { formatDate, formatTags } from '../shared/formatters';

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  name: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  item_type: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  tags: {
    type: 'array',
    editable: false,
    filterable: true,
    sortable: false,
    formatter: formatTags
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  }
};

// Column definitions
const columns: ColumnDefinition[] = [
  { key: 'name', label: 'Name', filterable: true, metadata: fields.name },
  { key: 'item_type', label: 'Type', filterable: true, metadata: fields.item_type },
  { key: 'tags', label: 'Tags', filterable: true, metadata: fields.tags },
  { key: 'updated_at', label: 'Last Updated', align: 'left', metadata: fields.updated_at }
];

// Configuration
const config: DataSourceConfig = {
  type: 'favorites',
  title: 'Favorites',
  description: 'Your favorite library items, documents, and templates',
  expandable: false,
};

// Data fetcher
function fetchData(selectedGame?: any, selectedFolderId?: string | null, user?: any): any[] {
  if (!selectedGame?.id || !user?.id) return [];
  
  const favorites = getCachedFavorites(user.id, selectedGame.id);
  
  // Fetch the actual items based on favorite references
  const favoriteItems = favorites.map((fav: any) => {
    if (fav.item_type === 'library') {
      const libraryItems = getCachedLibraryItems(selectedGame.id);
      const item = libraryItems.find((item: any) => item.id === fav.item_id);
      return item ? { ...item, favorite_id: fav.id, favorite_type: 'library', item_type: 'library' } : null;
    } else if (fav.item_type === 'document') {
      const documents = getCachedDocumentItems(selectedGame.id);
      const doc = documents.find((doc: any) => doc.id === fav.item_id);
      return doc ? { ...doc, favorite_id: fav.id, favorite_type: 'document', item_type: 'document' } : null;
    } else if (fav.item_type === 'blueprint') {
      const blueprints = getCachedTemplates(selectedGame.id);
      const bp = blueprints.find((bp: any) => bp.id === fav.item_id);
      return bp ? { ...bp, favorite_id: fav.id, favorite_type: 'blueprint', item_type: 'blueprint' } : null;
    }
    return null;
  }).filter(Boolean);
  
  return favoriteItems;
}

// Transformers
function toList(rawData: any[]): ListWidgetItem[] {
  return rawData.map(item => ({
    id: item.id,
    name: item.name,
    tags: formatTags(item.tags),
    type: item.item_type || 'Unknown',
    created_at: item.created_at,
    data: { 
      item_type: item.item_type,
      favorite_id: item.favorite_id
    },
    updated_at: item.updated_at,
    description: item.description || 'Favorite item'
  }));
}

function toTable(rawData: any[]): TableDataOutput {
  return {
    columns,
    data: rawData.map(item => ({
      id: item.id,
      name: String(item.name),
      item_type: String(item.item_type || 'Unknown'),
      tags: formatTags(item.tags),
      updated_at: formatDate(item.updated_at),
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

// Export the module
export const favoritesDataSource: DataSourceModule = {
  type: 'favorites',
  config,
  fields,
  columns,
  fetchData,
  transformers: {
    toList,
    toTable
  }
};

